import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp } from 'lucide-react';
import { trackCalculatorClick } from './analytics';

const ProductivityPopup = ({ 
  pageType = 'default', // 'homepage', 'services', 'blog', 'default'
  triggerDelay = 20000, // 20 seconds default
  scrollTrigger = 50 // 50% scroll
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    // Check if popup should show on this page type
    const highIntentPages = ['homepage', 'services', 'blog'];
    if (!highIntentPages.includes(pageType)) {
      return;
    }

    // Check session storage - once per session
    const shownThisSession = sessionStorage.getItem('productivityPopupShown');
    
    // Check localStorage - once per day
    const lastShown = localStorage.getItem('productivityPopupLastShown');
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    
    if (shownThisSession || (lastShown && parseInt(lastShown) > oneDayAgo)) {
      return;
    }

    // Time-based trigger
    const timeoutId = setTimeout(() => {
      if (!hasBeenShown) {
        setIsVisible(true);
        setHasBeenShown(true);
        sessionStorage.setItem('productivityPopupShown', 'true');
        localStorage.setItem('productivityPopupLastShown', Date.now().toString());
      }
    }, triggerDelay);

    // Scroll-based trigger
    const handleScroll = () => {
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercentage >= scrollTrigger && !hasBeenShown) {
        setIsVisible(true);
        setHasBeenShown(true);
        sessionStorage.setItem('productivityPopupShown', 'true');
        localStorage.setItem('productivityPopupLastShown', Date.now().toString());
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pageType, triggerDelay, scrollTrigger, hasBeenShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Icon */}
              <div className="w-16 h-16 bg-golden/20 flex items-center justify-center mb-6 mx-auto">
                <TrendingUp className="w-8 h-8 text-carbon" />
              </div>

              {/* Headline */}
              <h2 className="text-2xl md:text-3xl font-bold text-carbon text-center mb-4 font-heading">
                Is Your Team Operating at Full Capacity?
              </h2>

              {/* Body */}
              <p className="text-gray-600 text-center mb-6 leading-relaxed">
                Uncover hidden productivity risks with our free 4-minute diagnostic.
                Get a tailored analysis showing where your team may be losing time, energy, and output — and what it could be costing you.
              </p>

              {/* CTA Button */}
              <a
                href="https://calculator.kalobrate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-golden text-carbon px-8 py-4 text-lg font-bold hover:bg-golden/90 transition-all text-center shadow-lg"
                onClick={() => {
                  trackCalculatorClick('productivity_popup');
                }}
              >
                Start the Productivity Check
              </a>

              {/* Fine print */}
              <p className="text-xs text-gray-500 text-center mt-4">
                No email required &middot; Results in 4 minutes
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProductivityPopup;
